import { useEffect, useRef } from 'react'

import { useProgress } from 'hooks/useProgress'
import { Screenshot } from 'screens/VirtualMarketerOnboarding/BrandProfileWebsite/Screenshot'
import { LoadingProgresses } from 'screens/VirtualMarketerOnboarding/Loading/LoadingProgresses'

interface Props {
  url?: string
  handleBack: () => void
  cancelOnError?: boolean
  finishedLoading?: boolean
}

export const LoadingState = ({ url, handleBack, cancelOnError, finishedLoading }: Props) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const {
    start: startSeven,
    isRunning: isRunningSeven,
    progress: progressSeven,
  } = useProgress({ linearProgress: true, speed: 60, immediateComplete: false })
  const {
    start: startSix,
    isRunning: isRunningSix,
    progress: progressSix,
  } = useProgress({ linearProgress: true, speed: 60, immediateComplete: true, onDone: startSeven })
  const {
    start: startFive,
    isRunning: isRunningFive,
    progress: progressFive,
  } = useProgress({ linearProgress: true, speed: 60, immediateComplete: true, onDone: startSix })
  const {
    start: startFour,
    isRunning: isRunningFour,
    progress: progressFour,
  } = useProgress({ linearProgress: true, speed: 60, immediateComplete: true, onDone: startFive })
  const {
    start: startThree,
    isRunning: isRunningThree,
    progress: progressThree,
  } = useProgress({ linearProgress: true, speed: 60, immediateComplete: true, onDone: startFour })
  const {
    start: startTwo,
    isRunning: isRunningTwo,
    progress: progressTwo,
  } = useProgress({ linearProgress: true, speed: 60, immediateComplete: true, onDone: startThree })
  const {
    start: startOne,
    isRunning: isRunningOne,
    progress: progressOne,
  } = useProgress({ linearProgress: true, speed: 80, immediateComplete: true, onDone: startTwo })

  if (!isRunningOne && progressOne === 0) {
    startOne()
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  const progressOneText = isRunningOne ? 'Analyzing your website' : 'Analyze your website'
  const progressTwoText = isRunningTwo ? 'Examining website experience' : 'Examine website experience'
  const progressThreeText = isRunningThree ? 'Locating your business' : 'Locate your business'
  const progressFourText = isRunningFour ? 'Analyzing the market' : 'Analyze the market'
  const progressFiveText = isRunningFive ? 'Looking for competitors' : 'Look for competitors'
  const progressSixText = isRunningSix ? 'Reviewing social media presence' : 'Review social media presence'
  const progressSevenText = isRunningSeven ? 'Examining keywords' : 'Examine keywords'

  const progresses = [
    {
      title: progressOne === 100 ? 'Analyzed your website' : progressOneText,
      isRunning: isRunningOne,
      percentComplete: finishedLoading ? 100 : progressOne,
      element: <Screenshot url={url} handleBack={handleBack} cancelOnError={cancelOnError} />,
      lineHeight: 220,
    },
    {
      title: progressTwo === 100 ? 'Examined website experience' : progressTwoText,
      isRunning: isRunningTwo,
      percentComplete: finishedLoading ? 100 : progressTwo,
    },
    {
      title: finishedLoading || progressThree === 100 ? 'Located your business' : progressThreeText,
      isRunning: isRunningThree && !finishedLoading,
      percentComplete: finishedLoading ? 100 : progressThree,
    },
    {
      title: finishedLoading || progressFour === 100 ? 'Analyzed the market' : progressFourText,
      isRunning: isRunningFour && !finishedLoading,
      percentComplete: finishedLoading ? 100 : progressFour,
    },
    {
      title: finishedLoading || progressFive === 100 ? 'Looked for competitors' : progressFiveText,
      isRunning: isRunningFive && !finishedLoading,
      percentComplete: finishedLoading ? 100 : progressFive,
    },
    {
      title: finishedLoading || progressSix === 100 ? 'Reviewed social media presence ' : progressSixText,
      isRunning: isRunningSix && !finishedLoading,
      percentComplete: finishedLoading ? 100 : progressSix,
    },
    {
      title: finishedLoading || progressSeven === 100 ? 'Examined keywords ' : progressSevenText,
      isRunning: isRunningSeven && !finishedLoading,
      percentComplete: finishedLoading ? 100 : progressSeven,
    },
  ]

  return <LoadingProgresses progresses={progresses} finishedLoading={finishedLoading} showLoadingSpinner />
}
